import React, { useState, useMemo } from 'react'
import {
  Percent,
  DollarSign,
  TrendingUp,
  FileText,
  Store,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import GlobalHeader from '../components/GlobalHeader'
import { HeroKpiCard } from '../components/HeroKpiCard'
import {
  useRetailMargins,
  useRetailSKUBreakdown,
  useRetailSellInTrends,
} from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
} from 'recharts'

// Map DB slugs to display names
const RETAILER_DISPLAY_NAMES: Record<string, string> = {
  'macys': "Macy's",
  "Macy's": "Macy's",
  'ulta': 'Ulta',
  'Ulta': 'Ulta',
  'jcp': 'JCPenney',
  'JCP': 'JCPenney',
}

function retailerDisplayName(slug: string): string {
  return RETAILER_DISPLAY_NAMES[slug] || slug
}

// Retailer brand colors (keyed by DB slug)
const RETAILER_COLORS: Record<string, { bg: string; border: string; text: string; chart: string }> = {
  'macys': { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', chart: '#ef4444' },
  "Macy's": { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', chart: '#ef4444' },
  'ulta': { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', chart: '#f97316' },
  'Ulta': { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', chart: '#f97316' },
  'jcp': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', chart: '#3b82f6' },
  'JCP': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', chart: '#3b82f6' },
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toFixed(0)}`
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

type SortField = 'revenue' | 'margin_percent' | 'quantity' | 'margin_dollars'
type SortDir = 'asc' | 'desc'

export default function RetailDashboard() {
  const { selectedDateRange } = useFiscalWeekContext()

  const { data: margins, isLoading: marginsLoading, error: marginsError } = useRetailMargins(selectedDateRange)
  const { data: skuData, isLoading: skuLoading } = useRetailSKUBreakdown(undefined, selectedDateRange)
  const { data: trendsData, isLoading: trendsLoading } = useRetailSellInTrends('month', selectedDateRange)

  const [sortField, setSortField] = useState<SortField>('revenue')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [retailerFilter, setRetailerFilter] = useState<string>('all')

  // Sort & filter SKU data
  const sortedSKUs = useMemo(() => {
    if (!skuData?.skus) return []
    let filtered = skuData.skus
    if (retailerFilter !== 'all') {
      filtered = filtered.filter(s => s.retailer === retailerFilter)
    }
    return [...filtered].sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal
    })
  }, [skuData, sortField, sortDir, retailerFilter])

  // Format trend data for Recharts
  const chartData = useMemo(() => {
    if (!trendsData?.trends) return []
    return trendsData.trends.map(t => {
      const date = new Date(t.period)
      return {
        period: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        total_units: t.total_units,
        total_revenue: t.total_revenue,
        ...Object.fromEntries(
          Object.entries(t.by_retailer).map(([retailer, data]) => [
            `${retailer}_units`, data.units,
          ])
        ),
      }
    })
  }, [trendsData])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />
    return sortDir === 'desc'
      ? <ChevronDown className="w-3 h-3 ml-1" />
      : <ChevronUp className="w-3 h-3 ml-1" />
  }

  if (marginsError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalHeader title="Retail Intelligence" subtitle="Sell-in margin analytics" />
        <div className="max-w-7xl mx-auto px-8 py-12">
          <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
            <p className="text-red-600 font-medium">Failed to load retail data</p>
            <p className="text-sm text-gray-500 mt-2">{marginsError instanceof Error ? marginsError.message : 'Unknown error'}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader
        title="Retail Intelligence"
        subtitle="Sell-in margin analytics across brick & mortar partners"
      />

      <div className="max-w-7xl mx-auto px-8 py-6 space-y-6">

        {/* KPI Cards Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <HeroKpiCard
            label="Blended Margin"
            value={marginsLoading ? '...' : formatPercent(margins?.blended.margin_percent ?? 0)}
            icon={Percent}
          />
          <HeroKpiCard
            label="Total Revenue"
            value={marginsLoading ? '...' : formatCurrency(margins?.blended.revenue ?? 0)}
            icon={DollarSign}
          />
          <HeroKpiCard
            label="Total Margin"
            value={marginsLoading ? '...' : formatCurrency(margins?.blended.margin_dollars ?? 0)}
            icon={TrendingUp}
          />
          <HeroKpiCard
            label="Purchase Orders"
            value={marginsLoading ? '...' : formatNumber(margins?.blended.po_count ?? 0)}
            icon={FileText}
          />
        </div>

        {/* Retailer Margin Cards */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Retailer Margins</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {marginsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-4" />
                  <div className="h-8 bg-gray-200 rounded w-16 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-32" />
                </div>
              ))
            ) : (
              margins?.retailers.map(r => {
                const colors = RETAILER_COLORS[r.retailer] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', chart: '#6b7280' }
                return (
                  <div key={r.retailer} className={`bg-white border ${colors.border} rounded-xl p-6`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Store className={`w-5 h-5 ${colors.text}`} />
                        <h3 className={`font-semibold ${colors.text}`}>{retailerDisplayName(r.retailer)}</h3>
                      </div>
                      <span className={`text-2xl font-bold ${colors.text}`}>
                        {formatPercent(r.margin_percent)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Revenue</p>
                        <p className="font-semibold text-gray-900">{formatCurrency(r.revenue)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Margin $</p>
                        <p className="font-semibold text-gray-900">{formatCurrency(r.margin_dollars)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Units</p>
                        <p className="font-semibold text-gray-900">{formatNumber(r.total_units)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">POs</p>
                        <p className="font-semibold text-gray-900">{r.po_count}</p>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </section>

        {/* PO Trend Chart */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sell-In Trends</h2>
          {trendsLoading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-80 flex items-center justify-center text-gray-500">
              No trend data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 10, right: 30, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v: number) => formatNumber(v)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v: number) => formatCurrency(v)}
                />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name.includes('revenue') || name === 'total_revenue') return [formatCurrency(value), 'Revenue']
                    return [formatNumber(value), name.replace('_units', ' Units').replace('total_units', 'Total Units')]
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="total_units" name="Total Units" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" dataKey="total_revenue" name="Revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </section>

        {/* SKU Margin Breakdown Table */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">SKU Margin Breakdown</h2>
            <select
              value={retailerFilter}
              onChange={e => setRetailerFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white"
            >
              <option value="all">All Retailers</option>
              {margins?.retailers.map(r => (
                <option key={r.retailer} value={r.retailer}>{retailerDisplayName(r.retailer)}</option>
              ))}
            </select>
          </div>

          {skuLoading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-semibold text-gray-600">SKU</th>
                    <th className="text-left py-3 px-2 font-semibold text-gray-600">Product</th>
                    <th className="text-left py-3 px-2 font-semibold text-gray-600">Retailer</th>
                    <th
                      className="text-right py-3 px-2 font-semibold text-gray-600 cursor-pointer select-none"
                      onClick={() => handleSort('quantity')}
                    >
                      <span className="inline-flex items-center">Qty<SortIcon field="quantity" /></span>
                    </th>
                    <th
                      className="text-right py-3 px-2 font-semibold text-gray-600 cursor-pointer select-none"
                      onClick={() => handleSort('revenue')}
                    >
                      <span className="inline-flex items-center">Revenue<SortIcon field="revenue" /></span>
                    </th>
                    <th
                      className="text-right py-3 px-2 font-semibold text-gray-600 cursor-pointer select-none"
                      onClick={() => handleSort('margin_percent')}
                    >
                      <span className="inline-flex items-center">Margin %<SortIcon field="margin_percent" /></span>
                    </th>
                    <th
                      className="text-right py-3 px-2 font-semibold text-gray-600 cursor-pointer select-none"
                      onClick={() => handleSort('margin_dollars')}
                    >
                      <span className="inline-flex items-center">Margin $<SortIcon field="margin_dollars" /></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSKUs.map((sku, i) => {
                    const colors = RETAILER_COLORS[sku.retailer]
                    return (
                      <tr key={`${sku.sku}-${sku.retailer}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="py-2.5 px-2 font-mono text-xs text-gray-700">{sku.base_sku}</td>
                        <td className="py-2.5 px-2 text-gray-900 max-w-[200px] truncate">{sku.product_name}</td>
                        <td className="py-2.5 px-2">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colors?.bg ?? 'bg-gray-100'} ${colors?.text ?? 'text-gray-700'}`}>
                            {retailerDisplayName(sku.retailer)}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right text-gray-900">{formatNumber(sku.quantity)}</td>
                        <td className="py-2.5 px-2 text-right font-medium text-gray-900">{formatCurrency(sku.revenue)}</td>
                        <td className="py-2.5 px-2 text-right">
                          <span className={sku.margin_percent >= 80 ? 'text-green-600 font-semibold' : sku.margin_percent >= 70 ? 'text-emerald-600' : 'text-gray-900'}>
                            {formatPercent(sku.margin_percent)}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right text-gray-900">{formatCurrency(sku.margin_dollars)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {sortedSKUs.length === 0 && (
                <div className="py-8 text-center text-gray-500">No SKU data available</div>
              )}
            </div>
          )}
        </section>

      </div>
    </div>
  )
}
